// Delar som Min jakt-startsidan och markens sida delar (DR1 omgång 2, skiss A och B).
import React from 'react';
import { Home } from 'lucide-react';
import { api, type Mark, type MarkCalendarEvent } from '../../api';
import { RADIUS, TYPE, useTheme } from '../../theme';
import { Badge, Card, IconButton, Icon } from '../../components/ui';
import { markImg, GREEN_PLACEHOLDER } from '../../components';

const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const DAYS_SHORT = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];

/** "Lör 28 okt" */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

/** "06:30", eller tom sträng om händelsen saknar klockslag (midnatt). */
export function timeOf(iso: string): string {
  const d = new Date(iso);
  if (d.getHours() === 0 && d.getMinutes() === 0) return '';
  return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

/** Typ av kalenderhändelse som badge: jakt i accent, resten neutrala. */
export function EventTypeBadge({ type }: { type: string }) {
  const t = (type || '').toLowerCase();
  if (t === 'hunt' || t === 'jakt') return <Badge tone="accent">Jakt</Badge>;
  if (t === 'meeting' || t === 'möte') return <Badge>Möte</Badge>;
  if (t === 'boende') return <Badge tone="info">Boende</Badge>;
  return <Badge>Övrigt</Badge>;
}

/** Plats: kommun, annars län. */
export function markPlace(m: Mark, withCounty = false): string {
  const p = m.municipality || m.county || '';
  return withCounty && m.municipality && m.county && m.county !== m.municipality ? `${m.municipality}, ${m.county}` : p;
}

export const fmtHa = (ha?: number) => (ha ? `${ha.toLocaleString('sv-SE')} ha` : '');

/** Datumruta 44×48 i accentSoft (dag + månad). */
export function DateBox({ iso, tone = 'accent' }: { iso: string; tone?: 'accent' | 'ok' }) {
  const T = useTheme();
  const d = new Date(iso);
  return (
    <div aria-hidden style={{
      width: 44, height: 48, borderRadius: 10, flexShrink: 0,
      background: tone === 'ok' ? T.okSoft : T.accentSoft, color: tone === 'ok' ? T.ok : T.accentText,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
    }}>
      <span style={{ fontSize: 17, fontWeight: 800 }}>{d.getDate()}</span>
      <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', marginTop: 3, textTransform: 'uppercase' }}>{MONTHS_SHORT[d.getMonth()]}</span>
    </div>
  );
}

/** En händelse i en lista: datumruta, titel, undertext och valfri badge. Klickbar om `onClick` finns. */
export function EventRow({ ev, sub, trailing, onClick, first, tone }: {
  ev: MarkCalendarEvent; sub?: string; trailing?: React.ReactNode; onClick?: () => void; first?: boolean; tone?: 'accent' | 'ok';
}) {
  const T = useTheme();
  const body = (
    <>
      <DateBox iso={ev.startTime} tone={tone} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</div>
        {sub && <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 2 }}>{sub}</div>}
      </div>
      {trailing}
    </>
  );
  const style: React.CSSProperties = {
    display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', minHeight: 48,
    borderTop: first ? 'none' : `1px solid ${T.border}`, width: '100%',
  };
  if (!onClick) return <div style={style}>{body}</div>;
  return (
    <button type="button" onClick={onClick} style={{
      ...style, background: 'none', border: 'none', borderTop: style.borderTop, cursor: 'pointer',
      textAlign: 'left', fontFamily: 'inherit', color: 'inherit', borderRadius: 0,
    }}>{body}</button>
  );
}

/** Länkknapp i accenttext, t.ex. "Visa kalender →" i en sektionsrubrik. */
export function TextLink({ children, onClick, ...rest }: { children: React.ReactNode; onClick: () => void } & React.AriaAttributes) {
  const T = useTheme();
  return (
    <button type="button" onClick={onClick} {...rest} style={{
      background: 'none', border: 'none', padding: '0 4px', minHeight: 44, cursor: 'pointer', fontFamily: 'inherit',
      fontSize: 14, fontWeight: 700, color: T.accentText, whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

/** Markkort på Min jakt: bild 16:9, namn, ort · yta · roll och nästa händelse. Hemmamark växlas med knappen på bilden. */
export function MyMarkCard({ mark, role, isHome, nextEvent, onOpen, onToggleHome }: {
  mark: Mark; role?: string; isHome: boolean; nextEvent?: MarkCalendarEvent;
  onOpen: () => void; onToggleHome: () => void;
}) {
  const T = useTheme();
  const meta = [markPlace(mark) || 'Okänd plats', fmtHa(mark.areaHa), role].filter(Boolean).join(' · ');
  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={onOpen} aria-label={`Öppna ${mark.name}`} style={{
        display: 'block', width: '100%', padding: 0, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: T.text,
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.lg, overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', aspectRatio: '16 / 9', background: T.surface2 }}>
          <img src={markImg(mark)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => { (e.target as HTMLImageElement).src = GREEN_PLACEHOLDER; }} />
          {isHome && <Badge style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(20,28,16,.78)', color: '#fff' }}>Hemmamark</Badge>}
          {mark.isDemo && <Badge tone="info" style={{ position: 'absolute', bottom: 12, left: 12 }}>Demo</Badge>}
        </div>
        <div style={{ padding: '14px 16px 16px' }}>
          <div style={{ ...TYPE.title, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mark.name}</div>
          <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 2 }}>{meta}</div>
          {nextEvent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: `1px solid ${T.border}`, marginTop: 14, paddingTop: 12, fontSize: 13, color: T.textSub, minWidth: 0 }}>
              <Badge tone="accent">{shortDate(nextEvent.startTime)}</Badge>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextEvent.title}</span>
            </div>
          )}
        </div>
      </button>
      <IconButton variant="float" label={isHome ? 'Ta bort som hemmamark' : 'Sätt som hemmamark'} aria-pressed={isHome}
        onClick={onToggleHome}
        style={{ position: 'absolute', top: 8, right: 8, color: isHome ? T.accentText : T.text }}>
        <Icon as={Home} />
      </IconButton>
    </div>
  );
}

/** Kort med markens närmaste händelser (Karta-flikens högerkolumn, skiss B). Hämtar själv. */
export function UpcomingCard({ markId, onShowCalendar, max = 3 }: { markId: string; onShowCalendar?: () => void; max?: number }) {
  const T = useTheme();
  const [events, setEvents] = React.useState<MarkCalendarEvent[] | null>(null);
  React.useEffect(() => {
    let off = false;
    api.markCalendarEvents(markId).then((d) => { if (!off) setEvents(d); }).catch(() => { if (!off) setEvents([]); });
    return () => { off = true; };
  }, [markId]);
  if (!events) return null;
  const now = Date.now();
  const next = events.filter((e) => e.status !== 'completed' && new Date(e.startTime).getTime() >= now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()).slice(0, max);
  return (
    <Card padding={18}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '-10px 0 2px' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>Kommande</div>
        {onShowCalendar && <TextLink onClick={onShowCalendar}>Kalender →</TextLink>}
      </div>
      {next.length === 0
        ? <div style={{ ...TYPE.small, color: T.textMuted, padding: '6px 0 2px' }}>Inga planerade händelser.</div>
        : next.map((ev, i) => <EventRow key={ev.id} ev={ev} first={i === 0} sub={[timeOf(ev.startTime), ev.location].filter(Boolean).join(' · ') || undefined} />)}
    </Card>
  );
}
